import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../../api/apiClient';
import {
  FaTachometerAlt, 
  FaUsers, 
  FaGraduationCap,          
  FaPuzzlePiece,            
  FaSitemap,                
  FaKey,                    
  FaCommentDots,            
  FaExclamationTriangle,    
  FaLaptopCode,             
  FaAward,                  
  FaChartPie,               
  FaHeadset,
  FaCog,
  FaTicketAlt,
  FaClipboardCheck,
  FaToolbox,
  FaLayerGroup,
  FaUserTag,
  FaShieldAlt,
  FaChevronDown,
  FaChevronRight,
  FaCalendarAlt,
  FaTrophy,
  FaRobot,
  FaRoute
} from 'react-icons/fa';

import { useAuth } from '../../../contexts/AuthContext';

const SidebarMenu = ({ onTabClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const roleValue = user ? parseInt(user.role) : null;
  const isSuperAdmin = roleValue === 0;
  const canSeeSiteManagement = roleValue === 0 || roleValue === 2;
  const [expandedMenus, setExpandedMenus] = useState({});
  // null = not yet fetched; [] = no access; [ids...] = allowed IDs
  const [allowedMenuIds, setAllowedMenuIds] = useState(null);

  useEffect(() => {
    if (isSuperAdmin || roleValue === null) {
      setAllowedMenuIds(null); // super admin — no filter
      return;
    }
    api.accessControl.getByRole(roleValue)
      .then((res) => {
        if (res.data?.success) {
          const { full_access, menu_ids } = res.data.data;
          setAllowedMenuIds(full_access ? null : (menu_ids || []));
        }
      })
      .catch(() => setAllowedMenuIds([])); // on error default to no access
  }, [roleValue, isSuperAdmin]);

  // Returns true if this menu item (by id) should be visible for the current user
  const isMenuAllowed = (id) => {
    if (allowedMenuIds === null) return true; // super admin or not yet loaded
    return allowedMenuIds.includes(id);
  };

  const menuItems = [
    {
      id: 0,
      label: 'Dashboard',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 p-2 shadow-lg group-hover:shadow-purple-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaTachometerAlt className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/dashboard'
    },
    {
      id: 1,
      label: 'Manage Users',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 p-2 shadow-lg group-hover:shadow-blue-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaUsers className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/users'
    },
    {
      id: 2,
      label: 'Manage Courses',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 p-2 shadow-lg group-hover:shadow-emerald-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaGraduationCap className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/courses'
    },
    {
      id: 15,
      label: 'Manage Assessments',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-lime-500 to-lime-700 p-2 shadow-lg group-hover:shadow-lime-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaClipboardCheck className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/assessments'
    },
    {
      id: 8,
      label: 'Manage Virtual-Led Training',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-orange-700 p-2 shadow-lg group-hover:shadow-orange-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaLaptopCode className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/vlt'
    },
    {
      id: 23,
      label: 'Manage Learning Journeys',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-700 p-2 shadow-lg group-hover:shadow-violet-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaRoute className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/learning-journeys'
    },
    {
      id: 4,
      label: 'Manage Organizations',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 p-2 shadow-lg group-hover:shadow-amber-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaSitemap className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/organizations'
    },
    {
      id: 5,
      label: 'Manage License Requests',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-pink-700 p-2 shadow-lg group-hover:shadow-pink-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaKey className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/licenses'
    },
    {
      id: 13,
      label: 'Manage Vouchers',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500 to-fuchsia-700 p-2 shadow-lg group-hover:shadow-fuchsia-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaTicketAlt className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/vouchers'
    },
    {
      id: 6,
      label: 'Manage Forums',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 p-2 shadow-lg group-hover:shadow-cyan-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaCommentDots className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/forums'
    },
    {
      id: 10,
      label: 'Manage Surveys',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-violet-700 p-2 shadow-lg group-hover:shadow-violet-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaChartPie className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/surveys'
    },
    {
      id: 20,
      label: 'Event Calendar',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-violet-700 p-2 shadow-lg group-hover:shadow-violet-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaCalendarAlt className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/calendar'
    },
    {
      id: 11,
      label: 'Manage Support Tickets',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-rose-700 p-2 shadow-lg group-hover:shadow-rose-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaHeadset className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/support-tickets'
    },
    {
      id: 14,
      label: 'Reports Analytics',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-green-500 to-green-700 p-2 shadow-lg group-hover:shadow-green-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaChartPie className="w-5 h-5 text-white" />
        </div>
      ),
      path: '/admin/reports/users'
    },
    ...(isSuperAdmin
      ? [
          {
            id: 19,
            label: 'Role Based Access Control',
            icon: (
              <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-rose-700 p-2 shadow-lg group-hover:shadow-rose-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
                <FaShieldAlt className="w-5 h-5 text-white" />
              </div>
            ),
            path: '/admin/access-control'
          }
        ]
      : []),
    {
      id: 16,
      label: 'Master Setup',
      icon: (
        <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-sky-700 p-2 shadow-lg group-hover:shadow-sky-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
          <FaToolbox className="w-5 h-5 text-white" />
        </div>
      ),
      children: [
        {
          id: 3,
          label: 'Manage Skills',
          icon: (
            <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 p-2 shadow-lg group-hover:shadow-indigo-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
              <FaPuzzlePiece className="w-5 h-5 text-white" />
            </div>
          ),
          path: '/admin/skills'
        },
        {
          id: 17,
          label: 'Manage Group',
          icon: (
            <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 p-2 shadow-lg group-hover:shadow-teal-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
              <FaLayerGroup className="w-5 h-5 text-white" />
            </div>
          ),
          path: '/admin/groups'
        },
        {
          id: 18,
          label: 'Manage Role',
          icon: (
            <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 p-2 shadow-lg group-hover:shadow-cyan-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
              <FaUserTag className="w-5 h-5 text-white" />
            </div>
          ),
          path: '/admin/roles'
        },
        {
          id: 7,
          label: 'Flagged Forum Comments',
          icon: (
            <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-red-500 to-red-700 p-2 shadow-lg group-hover:shadow-red-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
              <FaExclamationTriangle className="w-5 h-5 text-white" />
            </div>
          ),
          path: '/admin/forums/moderation'
        },
        {
          id: 9,
          label: 'Certificate Templates',
          icon: (
            <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 p-2 shadow-lg group-hover:shadow-teal-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
              <FaAward className="w-5 h-5 text-white" />
            </div>
          ),
          path: '/admin/certificates/templates'
        },
        {
          id: 21,
          label: 'Global Learning Score & Badges',
          icon: (
            <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 p-2 shadow-lg group-hover:shadow-amber-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
              <FaTrophy className="w-5 h-5 text-white" />
            </div>
          ),
          path: '/admin/gamification'
        },
        {
          id: 22,
          label: 'EduGenie',
          icon: (
            <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-700 p-2 shadow-lg group-hover:shadow-violet-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
              <FaRobot className="w-5 h-5 text-white" />
            </div>
          ),
          path: '/admin/edugenie'
        }
      ]
    },
    // Access Control — Super Admin only

    ...(canSeeSiteManagement
      ? [
          {
            id: 12,
            label: 'Site Management',
            icon: (
              <div className="me-2 w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 p-2 shadow-lg group-hover:shadow-slate-500/50 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center">
                <FaCog className="w-5 h-5 text-white" />
              </div>
            ),
            path: '/admin/site-management'
          }
        ]
      : []),
  ];

  const isPathActive = (path) => {
    return location.pathname === path ||
      (path === '/admin/dashboard' && location.pathname === '/admin/dashboard') ||
      (path === '/admin/courses' &&
       (location.pathname.includes('/course-modules/') || location.pathname.includes('/course-enrollments/'))) ||
      (path === '/admin/skills' && location.pathname.startsWith('/admin/skills')) ||
      (path === '/admin/groups' && (location.pathname.startsWith('/admin/groups'))) ||
      (path === '/admin/roles' && location.pathname.startsWith('/admin/roles')) ||
      (path === '/admin/organizations' &&
       (location.pathname.includes('/organizations/') &&
        (location.pathname.includes('/users') || location.pathname.includes('/admins') || location.pathname.includes('/courses') || location.pathname.includes('/licenses')))) ||
      (path === '/admin/forums' &&
       (location.pathname.startsWith('/admin/forums') && !location.pathname.includes('/moderation'))) ||
      (path === '/admin/forums/moderation' && location.pathname.includes('/admin/forums/moderation')) ||
      (path === '/admin/vlt' && location.pathname.includes('/admin/vlt')) ||
      (path === '/admin/calendar' && location.pathname.includes('/admin/calendar')) ||
      (path === '/admin/certificates/templates' && location.pathname.includes('/admin/certificates')) ||
      (path === '/admin/gamification' && location.pathname.startsWith('/admin/gamification')) ||
      (path === '/admin/edugenie' && location.pathname.startsWith('/admin/edugenie')) ||
      (path === '/admin/surveys' && location.pathname.includes('/admin/surveys')) ||
      (path === '/admin/assessments' && location.pathname.includes('/admin/assessments')) ||
      (path === '/admin/learning-journeys' && location.pathname.includes('/admin/learning-journeys')) ||
      (path === '/admin/reports/users' && location.pathname.startsWith('/admin/reports')) ||
      (path === '/admin/site-management' && location.pathname.startsWith('/admin/site-management')) ||
      (path === '/admin/access-control' && location.pathname.startsWith('/admin/access-control'));
  };

  const handleNavigation = (item) => {
    navigate(item.path);
    onTabClick(item.id);
  };

  const toggleMenu = (id, fallbackExpanded) => {
    setExpandedMenus((prev) => {
      const current = prev[id] !== undefined ? prev[id] : fallbackExpanded;
      return { ...prev, [id]: !current };
    });
  };

  const renderLeaf = (item, isChild = false) => {
    const isActive = isPathActive(item.path);

    return (
      <div
        key={item.id}
        onClick={() => handleNavigation(item)}
        className={`
          flex items-center cursor-pointer font-medium text-sm
          min-h-12 px-2 py-2 rounded-xl group
          ${isChild ? 'ml-4' : ''}
          ${isActive
            ? 'bg-white/30 text-white shadow-md border-l-4 border-white '
            : 'text-white hover:bg-white/15 hover:text-white '
          }
        `}
      >
        <div className="w-5 h-5 flex items-center justify-center mr-3">
          {React.cloneElement(item.icon, {
            className: `text-base ${isActive ? 'text-white' : 'text-purple-200 group-hover:text-white'}`
          })}
        </div>
        <span className="flex-1 text-left leading-tight">{item.label}</span>
      </div>
    );
  };

  // Apply access control filter — Super Admin (allowedMenuIds === null) sees everything
  const visibleMenuItems = menuItems.reduce((acc, item) => {
    if (item.children) {
      const visibleChildren = item.children.filter((child) => isMenuAllowed(child.id));
      if (visibleChildren.length === 0) return acc;
      acc.push({ ...item, children: visibleChildren });
    } else {
      if (!isMenuAllowed(item.id)) return acc;
      acc.push(item);
    }
    return acc;
  }, []);

  return (
    <div className="py-3 px-2">
      {visibleMenuItems.map((item) => {
        if (item.children) {
          const hasActiveChild = item.children.some((child) => isPathActive(child.path));
          const isExpanded = expandedMenus[item.id] !== undefined ? expandedMenus[item.id] : hasActiveChild;

          return (
            <div key={item.id}>
              <div
                onClick={() => toggleMenu(item.id, hasActiveChild)}
                className={`
                  flex items-center cursor-pointer font-medium text-sm
                  min-h-12 px-2 py-2 rounded-xl group
                  ${hasActiveChild
                    ? 'bg-white/30 text-white shadow-md border-l-4 border-white '
                    : 'text-white hover:bg-white/15 hover:text-white '
                  }
                `}
              >
                <div className="w-5 h-5 flex items-center justify-center mr-3">
                  {React.cloneElement(item.icon, {
                    className: `text-base ${hasActiveChild ? 'text-white' : 'text-purple-200 group-hover:text-white'}`
                  })}
                </div>
                <span className="flex-1 text-left leading-tight">{item.label}</span>
                {isExpanded
                  ? <FaChevronDown className="w-3 h-3 text-purple-200" />
                  : <FaChevronRight className="w-3 h-3 text-purple-200" />}
              </div>
              {isExpanded && (
                <div className="mt-1 space-y-1">
                  {item.children.map((child) => renderLeaf(child, true))}
                </div>
              )}
            </div>
          );
        }

        return renderLeaf(item, false);
      })}
    </div>
  );
};

export default SidebarMenu;
